import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Card, CardBody, Table, Badge } from "reactstrap"
import api from "API/Api"
import { Link, useParams } from "react-router-dom"
import QRCode from "react-qr-code"
import SweetAlert from "react-bootstrap-sweetalert"

const VlccMobileLogin = () => {
  const { ccId } = useParams()
  const [center, setCenter] = useState(null)
  const [syncData, setSyncData] = useState(null)
  const [deviceData, setDeviceData] = useState(null)
  const [licenseKey, setLicenseKey] = useState(null)
  const [licenseState, setLicenseState] = useState('none')
  const [genLoading, setGenLoading] = useState(false)
  const [revokeConfirm, setRevokeConfirm] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  // Settings History filters
  const [historySearch, setHistorySearch] = useState("")
  const [historyFilter, setHistoryFilter] = useState("all") // all | pending | synced | unapproved | approved
  const [historyDateFrom, setHistoryDateFrom] = useState("")
  const [historyDateTo, setHistoryDateTo] = useState("")
  const [approvingId, setApprovingId] = useState(null)
  const [bulkApproving, setBulkApproving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const [ccResp, syncResp, devResp] = await Promise.all([
          api.get("collection-center/" + ccId),
          api.get("settings-sync-status/" + ccId),
          api.get("device/status/" + ccId)
        ])
        const data = ccResp.data.data?.[0] || ccResp.data.data
        setCenter(data)
        if (syncResp.data.success) setSyncData(syncResp.data)
        if (devResp.data.success) {
          setDeviceData(devResp.data)
          setLicenseState(devResp.data.license_state || 'none')
          if (devResp.data.existing_key) setLicenseKey(devResp.data.existing_key)
        }
      } catch (e) { console.error(e) }
      setPageLoad(false)
    }
    load()
  }, [ccId])

  const formatDate = d => {
    if (!d) return "Never"
    const dt = new Date(d)
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + " " + dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  }

  const generateLicense = async () => {
    setGenLoading(true)
    try {
      const resp = await api.post("device/generate-license", { cc_id: parseInt(ccId), center_type: 'vlcc' })
      if (resp.data.success) { setLicenseKey(resp.data.license_key); setLicenseState('unused') }
    } catch (e) { console.error(e) }
    setGenLoading(false)
  }

  const revokeLicense = async () => {
    setGenLoading(true); setRevokeConfirm(false)
    try {
      const resp = await api.post("device/revoke-license", { cc_id: parseInt(ccId), center_type: 'vlcc' })
      if (resp.data.success) {
        setLicenseKey(resp.data.license_key); setLicenseState('unused')
        // Refresh device list
        const devResp = await api.get("device/status/" + ccId)
        if (devResp.data.success) setDeviceData(devResp.data)
      }
    } catch (e) { console.error(e) }
    setGenLoading(false)
  }

  const handleLicenseAction = () => {
    if (licenseState === 'active') setRevokeConfirm(true)
    else generateLicense()
  }

  const licenseButtonLabel = licenseState === 'active' ? 'Regenerate License Key'
    : licenseState === 'unused' ? 'Show License Key' : 'Generate License Key'
  const licenseButtonIcon = licenseState === 'active' ? 'fa-sync-alt'
    : licenseState === 'unused' ? 'fa-clipboard' : 'fa-key'

  const timeSince = d => {
    if (!d) return "Never"
    const mins = Math.floor((new Date() - new Date(d)) / 60000)
    if (mins < 1) return "Just now"
    if (mins < 60) return mins + "m ago"
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return hrs + "h ago"
    return Math.floor(hrs / 24) + "d ago"
  }

  // Approve a single change
  const approveChange = async (logId) => {
    setApprovingId(logId)
    try {
      const resp = await api.post("approve-setting-change/" + logId, { approved_by: "Admin" })
      if (resp.data.success) {
        setSyncData(prev => ({
          ...prev,
          logs: prev.logs.map(l => l.id === logId ? { ...l, approved: true, approved_by: "Admin", approved_at: resp.data.approved_at } : l)
        }))
      }
    } catch (e) { console.error(e) }
    setApprovingId(null)
  }

  // Bulk approve all pending
  const bulkApprove = async () => {
    setBulkApproving(true)
    try {
      const resp = await api.post("bulk-approve-changes/" + ccId, { approved_by: "Admin" })
      if (resp.data.success) {
        const now = new Date().toISOString()
        setSyncData(prev => ({
          ...prev,
          logs: prev.logs.map(l => !l.approved ? { ...l, approved: true, approved_by: "Admin", approved_at: now } : l)
        }))
      }
    } catch (e) { console.error(e) }
    setBulkApproving(false)
  }

  // Filtered + searched logs
  const filteredLogs = (syncData?.logs || []).filter(log => {
    // Text search
    if (historySearch) {
      const s = historySearch.toLowerCase()
      const matchText = (log.changes_summary || "").toLowerCase().includes(s)
        || (log.updated_by || "").toLowerCase().includes(s)
        || (log.approved_by || "").toLowerCase().includes(s)
      if (!matchText) return false
    }
    // Status filter
    if (historyFilter === "pending" && log.synced_to_device) return false
    if (historyFilter === "synced" && !log.synced_to_device) return false
    if (historyFilter === "unapproved" && log.approved) return false
    if (historyFilter === "approved" && !log.approved) return false
    // Date range
    if (historyDateFrom && log.server_updated_at < historyDateFrom) return false
    if (historyDateTo && log.server_updated_at > historyDateTo + "T23:59:59") return false
    return true
  })

  const unapprovedCount = (syncData?.logs || []).filter(l => !l.approved).length

  const InfoRow = ({ icon, label, value, color }) => (
    <div style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
        background: `${color || "#6366f1"}15`, color: color || "#6366f1", marginRight: "12px", flexShrink: 0
      }}>
        <i className={`fas ${icon}`} style={{ fontSize: "14px" }}></i>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
        <div style={{ fontSize: "14px", color: "#1e293b", fontWeight: 500 }}>{value || "—"}</div>
      </div>
    </div>
  )

  const SectionTitle = ({ icon, title, color }) => (
    <div style={{ display: "flex", alignItems: "center", marginBottom: "12px", marginTop: "8px" }}>
      <div style={{
        width: 28, height: 28, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
        background: color, color: "#fff", marginRight: "10px", fontSize: "12px"
      }}>
        <i className={`fas ${icon}`}></i>
      </div>
      <h6 style={{ margin: 0, fontWeight: 700, color: "#334155", fontSize: "14px" }}>{title}</h6>
    </div>
  )

  if (pageLoad) return (
    <div className="dashBody"><div className="text-center py-5">
      <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
      <p className="mt-3 text-muted">Loading Center Details...</p>
    </div></div>
  )

  if (!center) return (
    <div className="dashBody"><div className="text-center py-5">
      <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3 d-block"></i>
      <p className="text-muted">Collection center not found</p>
      <Link to="/collection-center-vlcc" className="btn btn-primary btn-sm">Back to List</Link>
    </div></div>
  )

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}>
            <h4>
              <i className="fas fa-store-alt" style={{ marginRight: "8px", color: "#6366f1" }}></i>
              VLCC Details — {center.centerName}
            </h4>
          </Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <Link to={"/vlcc-mobile-settings/" + ccId} className="btn btn-outline-primary btn-sm me-2">
              <i className="fas fa-cog"></i> Mobile Settings
            </Link>
            <Link to={"/edit-collection-center/" + ccId} className="btn btn-outline-warning btn-sm me-2">
              <i className="fas fa-edit"></i> Edit
            </Link>
            <Link to="/collection-center-vlcc" className="dashBtn">
              <i className="fas fa-chevron-left"></i> <b>Back</b>
            </Link>
          </Col>
        </Row>
      </div>

      <div style={{ padding: "16px" }}>
        <Row>
          {/* Left: License Key & QR */}
          <Col md={4}>
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px" }}>
              <CardBody className="text-center" style={{ padding: "24px" }}>
                {licenseKey ? (
                  <>
                    <div style={{ background: "linear-gradient(135deg, #10b981, #059669)", borderRadius: "12px", padding: "20px", marginBottom: "16px" }}>
                      <div style={{ fontSize: "10px", color: "#d1fae5", fontWeight: 600, textTransform: "uppercase", marginBottom: "10px", letterSpacing: "1px" }}>
                        <i className="fas fa-check-circle me-1"></i>{licenseState === 'active' ? 'Active License Key' : 'License Key — Scan to Activate'}
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: "22px", fontWeight: 700, color: "#fff", letterSpacing: "2px", padding: "8px 12px", background: "rgba(255,255,255,0.15)", borderRadius: "8px", border: "2px dashed rgba(255,255,255,0.4)", display: "inline-block" }}>
                        {licenseKey}
                      </div>
                      <div style={{ marginTop: "14px" }}>
                        <div style={{ background: "#fff", borderRadius: "8px", padding: "10px", display: "inline-block" }}>
                          <QRCode size={140} value={licenseKey} viewBox="0 0 256 256" />
                        </div>
                      </div>
                    </div>
                    <p style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "12px" }}>Scan this QR or type the key in the mobile app</p>
                  </>
                ) : (
                  <div style={{ padding: "40px 20px" }}>
                    <i className="fas fa-tablet-alt fa-3x d-block mb-3" style={{ color: "#cbd5e1" }}></i>
                    <p className="text-muted mb-3">No license key generated yet</p>
                  </div>
                )}
                <button className={`btn btn-${licenseState === 'active' ? 'warning' : 'primary'} btn-sm w-100`}
                  onClick={handleLicenseAction} disabled={genLoading}
                  style={{ borderRadius: "8px", padding: "8px 16px", fontWeight: 600 }}>
                  {genLoading ? <Spinner size="sm" /> : <><i className={`fas ${licenseButtonIcon} me-1`}></i>{licenseButtonLabel}</>}
                </button>
                {/* Center Quick Stats */}
                <div style={{ marginTop: "16px", padding: "12px", background: "#f0fdf4", borderRadius: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-around" }}>
                    <div className="text-center">
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#16a34a" }}>{center.centerNumber || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Center #</div>
                    </div>
                    <div style={{ borderLeft: "1px solid #dcfce7" }}></div>
                    <div className="text-center">
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#16a34a" }}>{center.canNumber || "—"}</div>
                      <div style={{ fontSize: "11px", color: "#64748b" }}>Can #</div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Right: Full Details */}
          <Col md={8}>
            <Row>
              {/* Center Info */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-store-alt" title="Center Information" color="#6366f1" />
                    <InfoRow icon="fa-building" label="Center Name" value={center.centerName} color="#6366f1" />
                    <InfoRow icon="fa-hashtag" label="Center Number" value={center.centerNumber} color="#6366f1" />
                    <InfoRow icon="fa-barcode" label="Can Number" value={center.canNumber} color="#6366f1" />
                    <InfoRow icon="fa-snowflake" label="Chilling Center" value={center.chillingCenterName} color="#0ea5e9" />
                    <InfoRow icon="fa-route" label="Route" value={center.routeName} color="#22c55e" />
                    <InfoRow icon="fa-desktop" label="Virtual Center" value={center.virtualCenter === "1" ? "Yes" : "No"} color="#f59e0b" />
                  </CardBody>
                </Card>
              </Col>

              {/* Address */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-map-marker-alt" title="Address" color="#0ea5e9" />
                    <InfoRow icon="fa-door-open" label="Door No" value={center.door} color="#0ea5e9" />
                    <InfoRow icon="fa-home" label="Village" value={center.village} color="#0ea5e9" />
                    <InfoRow icon="fa-map" label="Taluk" value={center.taluk} color="#0ea5e9" />
                    <InfoRow icon="fa-city" label="District" value={center.districtName} color="#0ea5e9" />
                    <InfoRow icon="fa-flag" label="State" value={center.stateName} color="#0ea5e9" />
                    <InfoRow icon="fa-map-pin" label="Pincode" value={center.pincode} color="#0ea5e9" />
                  </CardBody>
                </Card>
              </Col>

              {/* People */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-user-tie" title="People & Management" color="#22c55e" />
                    <InfoRow icon="fa-user" label="Incharge Name" value={center.inchargeName} color="#22c55e" />
                    <InfoRow icon="fa-phone" label="Incharge Mobile" value={center.inchargeMobile} color="#22c55e" />
                    <InfoRow icon="fa-id-card" label="Incharge Aadhar" value={center.inchargeAadhar} color="#22c55e" />
                    <InfoRow icon="fa-address-card" label="Incharge PAN" value={center.inchargePAN} color="#22c55e" />
                    <InfoRow icon="fa-user-shield" label="Supervisor" value={center.supervisor} color="#22c55e" />
                  </CardBody>
                </Card>
              </Col>

              {/* Committee Members */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-users" title="Committee Members" color="#8b5cf6" />
                    <InfoRow icon="fa-user-tie" label="Secretary" value={center.secretaryName} color="#8b5cf6" />
                    <InfoRow icon="fa-phone" label="Secretary Mobile" value={center.secretaryMobile} color="#8b5cf6" />
                    <InfoRow icon="fa-crown" label="President" value={center.presidentName} color="#8b5cf6" />
                    <InfoRow icon="fa-phone" label="President Mobile" value={center.presidentMobile} color="#8b5cf6" />
                    <InfoRow icon="fa-laptop" label="VLCC Remote Login" value={center.vlccLogins} color="#8b5cf6" />
                  </CardBody>
                </Card>
              </Col>

              {/* Settings Overview */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-cog" title="Configuration" color="#f59e0b" />
                    <InfoRow icon="fa-chart-bar" label="Rate Chart Type" value={center.rateChartType === "0" ? "Common" : center.rateChartType === "1" ? "Separate" : center.rateChartType} color="#f59e0b" />
                    <InfoRow icon="fa-money-bill" label="Payment Mode" value={center.paymentMode === "0" ? "Cash" : center.paymentMode === "1" ? "Bank" : center.paymentMode} color="#f59e0b" />
                    <InfoRow icon="fa-tint" label="Default Milk Type" value={center.defaultMilkTypeCC === "1" ? "Cow" : center.defaultMilkTypeCC === "2" ? "Buffalo" : "None"} color="#f59e0b" />
                    <InfoRow icon="fa-sms" label="SMS for Purchases" value={center.sendSmsCcPurchases === "1" ? "Yes" : "No"} color="#f59e0b" />
                    <InfoRow icon="fa-truck" label="Pickup Vehicle" value={center.pickupVehicle} color="#f59e0b" />
                  </CardBody>
                </Card>
              </Col>

              {/* Export Parameters */}
              <Col md={6}>
                <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
                  <CardBody>
                    <SectionTitle icon="fa-file-export" title="Export Parameters" color="#ef4444" />
                    <InfoRow icon="fa-tag" label="Parameter 1" value={center.parameter1} color="#ef4444" />
                    <InfoRow icon="fa-tag" label="Parameter 2" value={center.parameter2} color="#ef4444" />
                    <InfoRow icon="fa-tag" label="Parameter 3" value={center.parameter3} color="#ef4444" />
                    <InfoRow icon="fa-ban" label="Limit Single Entry" value={center.limitSingleEntryVendorCC === "1" ? "Yes" : "No"} color="#ef4444" />
                    <InfoRow icon="fa-lock" label="Agent Comm. Cow" value={center.agentCommissionCowMilk === "1" ? "Yes" : "No"} color="#ef4444" />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
        {/* Settings Sync Status */}
        <Row>
          {/* Settings Summary Card */}
          <Col md={12}>
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                {/* Header Row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "8px", background: "#6366f115", color: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
                      <i className="fas fa-sync-alt" style={{ fontSize: "16px" }}></i>
                    </div>
                    <div>
                      <h6 style={{ margin: 0, fontWeight: 700, color: "#334155", fontSize: "16px" }}>Settings Sync Status</h6>
                      <p style={{ margin: 0, fontSize: "12px", color: "#94a3b8" }}>Tracks when settings are updated and synced to the mobile device</p>
                    </div>
                  </div>
                  <Badge color={syncData?.summary?.pending_changes > 0 ? "warning" : "success"} style={{ fontSize: "13px", padding: "6px 14px" }}>
                    {syncData?.summary?.pending_changes > 0
                      ? <><i className="fas fa-exclamation-triangle me-1"></i>{syncData.summary.pending_changes} Pending</>
                      : <><i className="fas fa-check-circle me-1"></i>All Synced</>
                    }
                  </Badge>
                </div>

                {/* Key Info Bar */}
                <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "220px", background: "#f8fafc", borderRadius: "8px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: "4px" }}>
                      <i className="fas fa-server" style={{ marginRight: "6px" }}></i>Last Updated in Server
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b" }}>
                      {formatDate(syncData?.summary?.last_server_update)}
                    </div>
                  </div>
                  <div style={{
                    flex: 1, minWidth: "220px", borderRadius: "8px", padding: "14px 16px",
                    background: syncData?.summary?.pending_changes > 0 ? "#fffbeb" : "#f0fdf4",
                    border: `1px solid ${syncData?.summary?.pending_changes > 0 ? "#fde68a" : "#bbf7d0"}`
                  }}>
                    <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: "4px" }}>
                      <i className="fas fa-mobile-alt" style={{ marginRight: "6px" }}></i>Updated in Center
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: syncData?.summary?.pending_changes > 0 ? "#d97706" : "#16a34a" }}>
                      {syncData?.summary?.pending_changes > 0 ? "No — Pending" : syncData?.summary?.last_device_sync ? "Yes" : "No"}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: "220px", background: "#faf5ff", borderRadius: "8px", padding: "14px 16px", border: "1px solid #e9d5ff" }}>
                    <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", marginBottom: "4px" }}>
                      <i className="fas fa-clock" style={{ marginRight: "6px" }}></i>Last Device Sync
                    </div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#7c3aed" }}>
                      {formatDate(syncData?.summary?.last_device_sync)}
                    </div>
                  </div>
                </div>

                {/* Stats Row */}
                <Row style={{ marginBottom: "20px" }}>
                  <Col md={4}>
                    <div style={{ background: "#f0f9ff", borderRadius: "8px", padding: "14px", textAlign: "center", border: "1px solid #bae6fd" }}>
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#0ea5e9" }}>{syncData?.summary?.total_changes || 0}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>Total Changes</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div style={{ background: "#f0fdf4", borderRadius: "8px", padding: "14px", textAlign: "center", border: "1px solid #bbf7d0" }}>
                      <div style={{ fontSize: "28px", fontWeight: 700, color: "#22c55e" }}>{syncData?.summary?.synced_changes || 0}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>Synced to Device</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div style={{
                      background: (syncData?.summary?.pending_changes || 0) > 0 ? "#fffbeb" : "#f0fdf4",
                      borderRadius: "8px", padding: "14px", textAlign: "center",
                      border: `1px solid ${(syncData?.summary?.pending_changes || 0) > 0 ? "#fde68a" : "#bbf7d0"}`
                    }}>
                      <div style={{ fontSize: "28px", fontWeight: 700, color: (syncData?.summary?.pending_changes || 0) > 0 ? "#f59e0b" : "#22c55e" }}>
                        {syncData?.summary?.pending_changes || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>Pending Sync</div>
                    </div>
                  </Col>
                </Row>

                {/* Change History Table */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
                  <h6 style={{ fontWeight: 700, color: "#475569", margin: 0, fontSize: "14px" }}>
                    <i className="fas fa-history" style={{ marginRight: "6px" }}></i>Settings Change History
                    <Badge color="info" style={{ marginLeft: "8px", fontSize: "11px" }}>{filteredLogs.length}</Badge>
                  </h6>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                    {unapprovedCount > 0 && (
                      <button className="btn btn-outline-success btn-sm" onClick={bulkApprove} disabled={bulkApproving}
                        style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "6px", fontWeight: 600 }}>
                        {bulkApproving ? <Spinner size="sm" /> : <><i className="fas fa-check-double me-1"></i>Approve All ({unapprovedCount})</>}
                      </button>
                    )}
                  </div>
                </div>

                {/* Search & Filters */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ flex: "1 1 220px", position: "relative" }}>
                    <i className="fas fa-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px" }}></i>
                    <input type="text" className="form-control form-control-sm" placeholder="Search changes, users..."
                      value={historySearch} onChange={e => setHistorySearch(e.target.value)}
                      style={{ paddingLeft: "32px", borderRadius: "6px", fontSize: "12px", border: "1px solid #e2e8f0" }} />
                  </div>
                  <select className="form-select form-select-sm" value={historyFilter} onChange={e => setHistoryFilter(e.target.value)}
                    style={{ width: "auto", minWidth: "130px", borderRadius: "6px", fontSize: "12px", border: "1px solid #e2e8f0" }}>
                    <option value="all">All Status</option>
                    <option value="pending">Pending Sync</option>
                    <option value="synced">Synced</option>
                    <option value="unapproved">Not Approved</option>
                    <option value="approved">Approved</option>
                  </select>
                  <input type="date" className="form-control form-control-sm" value={historyDateFrom} onChange={e => setHistoryDateFrom(e.target.value)}
                    style={{ width: "auto", minWidth: "130px", borderRadius: "6px", fontSize: "12px", border: "1px solid #e2e8f0" }} />
                  <span style={{ color: "#94a3b8", fontSize: "11px" }}>to</span>
                  <input type="date" className="form-control form-control-sm" value={historyDateTo} onChange={e => setHistoryDateTo(e.target.value)}
                    style={{ width: "auto", minWidth: "130px", borderRadius: "6px", fontSize: "12px", border: "1px solid #e2e8f0" }} />
                  {(historySearch || historyFilter !== "all" || historyDateFrom || historyDateTo) && (
                    <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: "11px", padding: "4px 8px", borderRadius: "6px" }}
                      onClick={() => { setHistorySearch(""); setHistoryFilter("all"); setHistoryDateFrom(""); setHistoryDateTo("") }}>
                      <i className="fas fa-times me-1"></i>Clear
                    </button>
                  )}
                </div>

                <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table className="table-hover mb-0" style={{ fontSize: "13px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                      <tr>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>#</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Change Description</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Updated By</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Server Updated</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Synced to Device</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Synced At</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Approved</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Approved By</th>
                        <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Approved Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.length > 0 ? filteredLogs.map((log, i) => (
                        <tr key={log.id} style={{ background: !log.synced_to_device ? "#fffbeb08" : "" }}>
                          <td style={{ color: "#94a3b8" }}>{i + 1}</td>
                          <td>
                            <div style={{ fontWeight: 500, color: "#1e293b", maxWidth: "280px" }}>{log.changes_summary}</div>
                          </td>
                          <td>
                            <span style={{ background: "#f0f9ff", color: "#0369a1", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                              <i className="fas fa-user" style={{ marginRight: "4px", fontSize: "10px" }}></i>{log.updated_by || "System"}
                            </span>
                          </td>
                          <td style={{ fontSize: "12px", color: "#64748b" }}>{formatDate(log.server_updated_at)}</td>
                          <td>
                            {log.synced_to_device
                              ? <Badge color="success" style={{ fontSize: "11px" }}><i className="fas fa-check me-1"></i>Yes</Badge>
                              : <Badge color="warning" style={{ fontSize: "11px" }}><i className="fas fa-clock me-1"></i>Pending</Badge>
                            }
                          </td>
                          <td style={{ fontSize: "12px", color: "#64748b" }}>{formatDate(log.synced_at)}</td>
                          <td>
                            {log.approved
                              ? <Badge color="success" style={{ fontSize: "11px" }}><i className="fas fa-check-circle me-1"></i>Approved</Badge>
                              : <button className="btn btn-outline-primary btn-sm" style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "4px" }}
                                  onClick={() => approveChange(log.id)} disabled={approvingId === log.id}>
                                  {approvingId === log.id ? <Spinner size="sm" /> : <><i className="fas fa-thumbs-up me-1"></i>Approve</>}
                                </button>
                            }
                          </td>
                          <td>
                            {log.approved_by ? (
                              <span style={{ background: "#f0fdf4", color: "#15803d", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                                <i className="fas fa-user-check" style={{ marginRight: "4px", fontSize: "10px" }}></i>{log.approved_by}
                              </span>
                            ) : <span style={{ color: "#cbd5e1", fontSize: "12px" }}>—</span>}
                          </td>
                          <td style={{ fontSize: "12px", color: "#64748b" }}>{formatDate(log.approved_at)}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={9} className="text-center py-4">
                          {(syncData?.logs?.length || 0) > 0 ? (
                            <><i className="fas fa-filter fa-2x d-block mb-2" style={{ color: "#cbd5e1" }}></i>
                            <span className="text-muted">No results match your filters</span></>
                          ) : (
                            <><i className="fas fa-check-circle fa-2x text-success d-block mb-2"></i>
                            <span className="text-muted">No changes made yet — all settings are at defaults</span></>
                          )}
                        </td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ═══════════ DEVICE STATUS ═══════════ */}
        <Row>
          <Col md={12}>
            <Card style={{ border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: "12px", marginBottom: "16px" }}>
              <CardBody style={{ padding: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "8px", background: "#10b98115", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
                    <i className="fas fa-tablet-alt" style={{ fontSize: "16px" }}></i>
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, color: "#334155", fontSize: "16px" }}>Registered Devices</h6>
                    <p style={{ margin: 0, fontSize: "12px", color: "#94a3b8" }}>Mobile app devices linked to this collection center</p>
                  </div>
                </div>

                {/* Version Status Bar */}
                {deviceData?.versions && (
                  <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                    {[{label: "Config Version", val: deviceData.versions.config, icon: "fa-cog", color: "#6366f1"},
                      {label: "Rate Version", val: deviceData.versions.rate, icon: "fa-chart-bar", color: "#0ea5e9"},
                      {label: "Producer Version", val: deviceData.versions.producer, icon: "fa-users", color: "#22c55e"}].map((v, i) => (
                      <div key={i} style={{ flex: 1, minWidth: "150px", background: `${v.color}08`, borderRadius: "8px", padding: "12px", border: `1px solid ${v.color}25`, textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: 700, color: v.color }}>v{v.val}</div>
                        <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}><i className={`fas ${v.icon} me-1`}></i>{v.label}</div>
                      </div>
                    ))}
                    {/* Queue Stats */}
                    <div style={{ flex: 1, minWidth: "150px", background: deviceData.queue?.pending > 0 ? "#fffbeb" : "#f0fdf4", borderRadius: "8px", padding: "12px", border: `1px solid ${deviceData.queue?.pending > 0 ? "#fde68a" : "#bbf7d0"}`, textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: deviceData.queue?.pending > 0 ? "#f59e0b" : "#22c55e" }}>{deviceData.queue?.pending || 0}</div>
                      <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}><i className="fas fa-cloud-upload-alt me-1"></i>Pending Uploads</div>
                    </div>
                  </div>
                )}

                {/* Devices Table */}
                {deviceData?.devices?.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" style={{ fontSize: "13px" }}>
                      <thead style={{ background: "#f8fafc" }}>
                        <tr>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Device</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Status</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Last Heartbeat</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Last Sync</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Last Purchase</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Battery</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>App Ver</th>
                          <th style={{ fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase" }}>Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deviceData.devices.map(dev => (
                          <tr key={dev.id}>
                            <td>
                              <div style={{ fontWeight: 600, color: "#1e293b" }}>{dev.device_name || "Unknown Device"}</div>
                              <div style={{ fontSize: "10px", color: "#94a3b8", fontFamily: "monospace" }}>{dev.device_uuid ? dev.device_uuid.substring(0, 16) + "..." : "Awaiting activation"}</div>
                            </td>
                            <td>
                              <Badge color={dev.is_online ? "success" : "secondary"} style={{ fontSize: "11px", padding: "4px 8px" }}>
                                <i className={`fas ${dev.is_online ? "fa-circle" : "fa-circle"} me-1`} style={{ fontSize: "6px", verticalAlign: "middle" }}></i>
                                {dev.is_online ? "Online" : "Offline"}
                              </Badge>
                            </td>
                            <td style={{ fontSize: "12px", color: "#64748b" }}>{timeSince(dev.last_heartbeat_at)}</td>
                            <td style={{ fontSize: "12px", color: "#64748b" }}>{timeSince(dev.last_sync_at)}</td>
                            <td style={{ fontSize: "12px", color: "#64748b" }}>{timeSince(dev.last_purchase_at)}</td>
                            <td>
                              {dev.battery_level != null ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <i className={`fas fa-battery-${dev.battery_level > 75 ? "full" : dev.battery_level > 50 ? "three-quarters" : dev.battery_level > 25 ? "half" : "quarter"}`}
                                    style={{ color: dev.battery_level > 25 ? "#22c55e" : "#ef4444" }}></i>
                                  <span style={{ fontSize: "12px" }}>{Math.round(dev.battery_level)}%</span>
                                </div>
                              ) : "—"}
                            </td>
                            <td><span style={{ fontSize: "11px", background: "#f0f9ff", color: "#0369a1", padding: "2px 6px", borderRadius: "4px" }}>{dev.app_version || "—"}</span></td>
                            <td>
                              {(dev.pending_uploads || 0) > 0 ?
                                <Badge color="warning" style={{ fontSize: "11px" }}>{dev.pending_uploads}</Badge> :
                                <span style={{ color: "#22c55e", fontSize: "12px" }}>✓ 0</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-tablet-alt fa-2x d-block mb-2" style={{ color: "#cbd5e1" }}></i>
                    <span className="text-muted">No devices registered yet. Generate a license key to connect a device.</span>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {revokeConfirm && (
        <SweetAlert title="Regenerate License Key?" warning showCancel
          confirmBtnText="Regenerate" confirmBtnBsStyle="danger" cancelBtnBsStyle="secondary"
          onConfirm={revokeLicense} onCancel={() => setRevokeConfirm(false)}>
          <p>This will:</p>
          <ul style={{ textAlign: "left", fontSize: "14px" }}>
            <li>Deactivate the current device</li>
            <li>Revoke the existing license key</li>
            <li>Generate a new key for this center</li>
          </ul>
          <p style={{ color: "#dc2626", fontWeight: 600 }}>The old device will NOT be able to sync until re-activated.</p>
        </SweetAlert>
      )}
    </div>
  )
}

export default VlccMobileLogin
